import diff from 'changeset';
import { cloneDeep, differenceWith, isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { NodeType } from '@amplify/amp-module';

import {
  ResetStore,
  SetAddingNode,
  SetSelectedNodeId,
  SetZoom,
  DecrementUndoHead,
  IncrementUndoHead,
  UpdateUndoStack,
  ResetChangesetState,
  ToggleDragging,
  SetApplyingChangesetState,
  SetHighlightedNode,
  DeleteConversations,
  UpdateAmpData,
  SetSelectedConversationId,
  SetCenterOnNode,
  SetNodesForDeletion,
  ToggleMultiDeletePopup,
  ForceRebuild,
  SetOpenedConversation,
  SetSelectedSubNode,
  SetNodesClipboard,
} from '@/constants/mutations';

import {
  AddToUndoStack,
  ApplyChangeset,
  ClearAddingNode,
  ClearStore,
  Redo,
  Undo,
  CreateTrigger,
  DeleteTriggerById,
  UpdateTrigger,
  UpdateAmp,
  DeleteConversationById,
  HandleConversationTitleChange,
  ReorderConversations,
  CreateNewConversation,
  AddNode,
  UpdateNodeData,
  DeleteNodesById,
  SafelyDeleteNodesById,
  SetCurrentConversation,
  AddSlideToConversation,
  CopyNodes,
  PasteNodes,
  CutNodes,
} from '@/constants/actions';

import {
  CanUndo,
  CanRedo,
  GetApplyChangesState,
  GetAddingNode,
  GetDragging,
  GetRedoChangeset,
  GetSelectedNodeId,
  GetUndoChangeset,
  GetZoom,
  GetCurrentConversations,
  GetCurrentConversation,
  GetCurrentAmp,
  GetCurrentConversationsList,
  GetConversationById,
  GetConversationsList,
  GetCurrentConversationId,
  GetNextConversationSortIndex,
  GetNextLinkSortIndex,
  GetNodeById,
  GetSelectedConversationNodes,
  GetConversationNodesById,
  GetSelectedConversationNodesArray,
  GetConversationEdgesById,
  GetCurrentConversationEdges,
  GetChildNodesByParentId,
  GetSelectedConversationTriggers,
  GetSelectedSubconversations,
  GetTriggers,
  GetSelectedSubNode,
} from '@/constants/getters';

import {
  findPreviousConversation,
  updateProperties,
  objectToSortedArray,
  createConversationObject,
  updateNodeIndexes,
  removeElement,
  findNextNodes,
} from '@/utils/amp-utils';

import { initNodeId, insertNodeIntoTree, mendTreeAroundNode, parseToEdges } from '@/utils/builder-utils';

const defaultState = () => {
  return {
    addingNode: null,
    dragging: false,
    selectedNodeId: null,
    selectedSubNodeIndex: -1,
    zoom: 100,
    undoHead: 0,
    undoStack: [],
    isApplyingChangeset: false,
    highlightedNode: null,
    centerOn: null,
    selectedConversationId: '',
    reBuildIncrementer: 0,
    nodesClipboard: [],
  };
};

export const state = defaultState;

export const mutations = {
  [ResetStore]: (state) => {
    Object.assign(state, defaultState());
  },

  [SetAddingNode]: (state, data) => {
    state.addingNode = data;
  },

  [SetZoom]: (state, data) => {
    state.zoom = data;
  },

  [SetSelectedNodeId]: (state, data) => {
    state.selectedNodeId = data || null;
  },

  [SetSelectedSubNode]: (state, data) => {
    state.selectedSubNodeIndex = data ?? -1;
  },

  [ToggleDragging]: (state, dragState) => {
    state.dragging = dragState !== undefined ? dragState : !state.dragging;
  },

  [UpdateUndoStack]: (state, data) => {
    const pointerDiff = state.undoStack.length - state.undoHead;

    if (pointerDiff) {
      state.undoStack.splice(state.undoHead, pointerDiff);
    }

    state.undoHead = state.undoStack.push(data);
  },

  [DecrementUndoHead]: (state) => {
    state.undoHead = state.undoHead - 1;
  },

  [IncrementUndoHead]: (state) => {
    state.undoHead = state.undoHead + 1;
  },

  [SetApplyingChangesetState]: (state) => {
    state.isApplyingChangeset = true;
  },

  [ResetChangesetState]: (state) => {
    state.isApplyingChangeset = false;
  },

  [SetHighlightedNode]: (state, id) => {
    state.highlightedNode = id;
  },

  /**
   * CONVERSATION MUTATIONS
   * @SetSelectedConversationId used to navigate between conversatoins.
   * @SetCenterOnNode also used when changing between conversations.
   */

  [SetSelectedConversationId](state, id) {
    state.selectedConversationId = id;
  },

  [SetCenterOnNode](state, id) {
    if (id && id.includes('_port_')) {
      state.centerOn = id.split('_port_')[0];
    } else {
      state.centerOn = id;
    }
  },

  [SetNodesClipboard](state, nodes) {
    state.nodesClipboard = nodes;
  },

  [ForceRebuild](state) {
    state.reBuildIncrementer += 1;
  },
};

export const actions = {
  [ClearStore]({ commit }) {
    commit(ResetStore);
  },

  [ClearAddingNode]({ commit }) {
    commit(SetAddingNode, null);
    commit(ToggleDragging, false);
  },

  /**
   * Actions created to call the following
   * mutations from the ampStore:
   * @UpdateAmp
   * @UpdateAmpData
   */

  [UpdateAmpData]({ commit }, data) {
    commit(`ampStore/${UpdateAmpData}`, data, { root: true });
  },

  [UpdateAmp]({ commit }, data) {
    commit(`ampStore/${UpdateAmp}`, data, { root: true });
  },

  /**
   * CONVERSATION ACTIONS
   * @SetCurrentConversation
   * @HandleConversationTitleChange changes name of the conversation based on input.
   * @CreateNewConversation
   * @DeleteConversationById
   * @ReorderConversations
   */

  [SetCurrentConversation]({ commit }, id) {
    commit(SetSelectedConversationId, id);
  },

  [HandleConversationTitleChange]({ getters, dispatch }, { id, value }) {
    const conversation = getters[GetConversationById](id);
    conversation.name = value;
    conversation.changed = true;
    dispatch(UpdateAmpData, { conversations: [conversation] });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
  },

  [CreateNewConversation]({ commit, getters, dispatch }, { parentId = null, sortIndex }) {
    const nextSortIndex = getters[GetNextConversationSortIndex];
    const newConversation = createConversationObject({ parentId, sortIndex: sortIndex || nextSortIndex });

    dispatch(UpdateAmpData, { conversations: [newConversation] });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
    commit(`sidePanelStore/${SetOpenedConversation}`, newConversation.id, { root: true });
    commit(SetSelectedConversationId, newConversation.id);
  },

  [DeleteConversationById]({ getters, commit, dispatch }, id) {
    const conversation = getters[GetConversationById](id);
    const conversations = getters[GetCurrentConversations];

    const selectId = findPreviousConversation(conversation, conversations);
    const conversationsToBeRemoved = [
      ...Object.values(conversations).filter((c) => c.parentId === conversation.id),
      conversation,
    ];

    if (Object.keys(conversations).length <= conversationsToBeRemoved.length) {
      console.error('The last conversation cannot be deleted');
      return;
    }

    commit(`ampStore/${DeleteConversations}`, { conversations: conversationsToBeRemoved }, { root: true });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
    commit(SetSelectedConversationId, selectId);
  },

  [AddSlideToConversation]({ dispatch, getters }, { id, slide }) {
    const currentAmp = getters[GetCurrentAmp];
    const conversation = { ...currentAmp.data.conversations[id] };

    if (!conversation.changed) conversation.name = slide.name;

    conversation.slide = slide.keyPath;
    conversation.keyId = slide.keyId;

    dispatch(UpdateAmpData, { conversations: [conversation] });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
  },

  [ReorderConversations]({ getters, dispatch }, conversations) {
    const newConversations = {};

    // Updating sortIndex on all parent conversations
    // We need to complete this loop before setting sortIndex on subConvs to ensure
    // that the sortIndex is updated correctly on the parent
    conversations.forEach((conv, index) => {
      if (!conv.parentId) {
        conv.sortIndex = index;
        newConversations[conv.id] = conv;
      }
    });

    // Updating sortIndex on subconversations based on parent conv
    const parentIdSortIndexes = {};
    conversations.forEach((conv) => {
      if (conv.parentId) {
        parentIdSortIndexes[conv.parentId] = newConversations[conv.parentId].sortIndex;
        parentIdSortIndexes[conv.parentId]++;
        conv.sortIndex = parentIdSortIndexes[conv.parentId];
        newConversations[conv.id] = conv;
      }
    });

    dispatch(UpdateAmpData, { conversations: objectToSortedArray(newConversations) });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
  },

  /**
   * UNDO REDO ACTIONS
   * Actions listed for performing the following
   * undo redo in the stack / history .
   * @Undo undo action
   * @Redo redo action
   * @AddToUndoStack pushes to undo stack
   * @ApplyChangeset
   */
  [Undo]({ commit, dispatch, getters }) {
    const undoChangeset = getters[GetUndoChangeset];

    commit(DecrementUndoHead);
    commit(SetApplyingChangesetState);
    dispatch(ApplyChangeset, undoChangeset);
  },

  [Redo]({ commit, dispatch, getters }) {
    const redoChangeset = getters[GetRedoChangeset];

    commit(IncrementUndoHead);
    commit(SetApplyingChangesetState);
    dispatch(ApplyChangeset, redoChangeset);
  },

  [AddToUndoStack]({ commit, getters }, data) {
    const isApplyingChangeset = getters[GetApplyChangesState];

    if (!isApplyingChangeset) {
      commit(UpdateUndoStack, data);
    } else {
      commit(ResetChangesetState);
    }
  },

  [ApplyChangeset]({ commit, state, dispatch, rootGetters }, changeset) {
    const currentAmp = rootGetters[`ampStore/${GetCurrentAmp}`];
    const ampData = { conversations: cloneDeep(currentAmp.data.conversations) };

    const outsideConversationChangeIndex = changeset.findIndex(
      (c) => c.key[0] === 'conversations' && c.key[1] !== state.selectedConversationId
    );

    if (outsideConversationChangeIndex !== -1) {
      commit(SetSelectedConversationId, changeset[outsideConversationChangeIndex].key[1]);
    }

    diff.apply(changeset, ampData, true);

    // If the ApplyChangeSet caused the current conversation to get deleted
    if (!Object.keys(ampData.conversations).includes(state.selectedConversationId)) {
      const conversations = cloneDeep(currentAmp.data.conversations);
      const currentConversation = conversations[state.selectedConversationId] || {};
      const selectedConversationId =
        findPreviousConversation(currentConversation, conversations) ||
        objectToSortedArray(ampData.conversations ?? {})[0]?.id;

      commit(`ampStore/${DeleteConversations}`, { conversations: [currentConversation] }, { root: true });
      commit(SetSelectedConversationId, selectedConversationId);
    }

    const oldNodes = objectToSortedArray(currentAmp.data.conversations[state.selectedConversationId]?.nodes);
    const currentNodes = objectToSortedArray(ampData.conversations[state.selectedConversationId]?.nodes);
    // find the differences between nodes and get the node id that should be focused
    // the parent node should be focused when the node is removed, otherwise the added/edited node should be focused
    const nodeId =
      currentNodes.length >= oldNodes.length
        ? differenceWith(currentNodes, oldNodes, isEqual)[0]?.id
        : differenceWith(oldNodes, currentNodes, isEqual)[0]?.parentId?.split('_')[0];

    dispatch(UpdateAmpData, { conversations: objectToSortedArray(ampData.conversations) });

    const conversationId = state.selectedConversationId;
    // Using transform to update the graph and nodes therein after we've updated the amp data
    this.$builder.getGraph(null, conversationId)?.transform({ nodeId, conversationId });
  },

  /**
   * TRIGGER ACTIONS
   * Actions listed for creating, updating
   * and deleting triggers by id.
   * @CreateTrigger
   * @UpdateTrigger
   * @DeleteTriggerById
   */

  [CreateTrigger]({ getters, dispatch, rootGetters }, data) {
    const conversation = getters[GetCurrentConversation];
    const id = uuidv4();
    conversation.triggers[id] = { id, ...data };

    dispatch(UpdateAmpData, { conversations: [conversation] });
    dispatch(UpdateAmp, rootGetters[`ampStore/${GetCurrentAmp}`]);
  },

  [UpdateTrigger]({ getters, dispatch }, { id, data }) {
    const conversation = getters[GetCurrentConversation];
    const trigger = conversation.triggers?.[id];

    updateProperties(trigger, data);

    dispatch(UpdateAmpData, { conversations: [conversation] });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
  },

  [DeleteTriggerById]({ getters, dispatch }, id) {
    const conversation = getters[GetCurrentConversation];
    const deletedTrigger = conversation.triggers?.[id];

    if (deletedTrigger) {
      delete conversation.triggers[id];
    }

    dispatch(UpdateAmpData, { conversations: [conversation] });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
  },

  /**
   * NODE ACTIONS
   * @AddNode creates a new node using @UpdateAmpData
   * @UpdateNodeData upated node data using @UpdateAmpData & @UpdateAmp
   * @DeleteNodesById deletes specified nodes using @UpdateAmpData & @UpdateAmp
   */

  [AddNode]({ getters, commit, dispatch }, data) {
    const conversation = getters[GetCurrentConversation];

    if (data.parentId && conversation.nodes) {
      insertNodeIntoTree(conversation.nodes, data);
    }

    conversation.nodes = updateNodeIndexes({ ...conversation.nodes, [data.id]: data });
    dispatch(UpdateAmpData, { conversations: [conversation] });
    commit(SetSelectedNodeId, data.id);
    commit(SetSelectedSubNode, -1);
  },

  [UpdateNodeData]({ getters, dispatch }, { data }) {
    const conversation = getters[GetCurrentConversation];
    const node = conversation.nodes?.[data.id];
    updateProperties(node, data, true);
    dispatch(UpdateAmpData, { conversations: [conversation] });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
  },

  [DeleteNodesById]({ commit, getters, dispatch }, ids) {
    const conversation = getters[GetCurrentConversation];
    const [firstNodeId, subNodeId] = ids[0].split('_port_');
    const firstNode = conversation.nodes?.[firstNodeId];

    commit(SetCenterOnNode, subNodeId ? firstNodeId : firstNode.parentId);

    ids.forEach((id) => {
      if (id.includes('_port_')) {
        const [parentId, subNodeId] = id.split('_port_');
        const parentNode = conversation.nodes?.[parentId];
        if (parentNode) {
          removeElement(parentNode.subNodes, parentNode.subNodes[subNodeId]);
        }
        return;
      }

      const deletedNode = conversation.nodes?.[id];
      if (deletedNode) {
        mendTreeAroundNode(conversation.nodes, deletedNode);
        delete conversation.nodes[id];
      }
    });

    dispatch(UpdateAmpData, { conversations: [conversation] });
    dispatch(UpdateAmp, getters[GetCurrentAmp]);
  },

  [SafelyDeleteNodesById]({ commit, getters, dispatch }, { nodes = [], deleteChildren = false }) {
    const conversation = getters[GetCurrentConversation];
    const nodesArray = getters[GetSelectedConversationNodesArray];

    let showWarning = false;
    const deleteIds = [];

    nodes.forEach((nodeItem) => {
      const { subNodeId } = nodeItem;
      const nodeId = ['string', 'number'].includes(typeof nodeItem) ? nodeItem : nodeItem.nodeId;
      const node = conversation.nodes?.[nodeId];

      if (!node) {
        return;
      }

      const subNodeProvided = ![null, undefined].includes(subNodeId);
      const deletingSubNode = subNodeProvided && Object.keys(node.subNodes || {}).length > 1;
      const parentId = deletingSubNode ? `${nodeId}_port_${subNodeId}` : nodeId;
      const hasSubNodes = Object.keys(node.subNodes || {}).length > 0;

      const checkForNextNode =
        deletingSubNode || deleteChildren || hasSubNodes || [NodeType.condition].includes(node.type);
      const nextNodeLengthCheck = deletingSubNode || deleteChildren ? 0 : 1;

      deleteIds.push(parentId);

      // We'll check for follow-up nodes to the subNodeId if it has been provided
      if (checkForNextNode && findNextNodes(nodesArray, parentId).length > nextNodeLengthCheck) {
        showWarning = true;

        const childNodesIds = getters[GetChildNodesByParentId]({ parentId });
        deleteIds.push(...childNodesIds);
      }
    });

    if (showWarning) {
      commit(`popupStore/${SetNodesForDeletion}`, deleteIds, { root: true });
      commit(`popupStore/${ToggleMultiDeletePopup}`, true, { root: true });
    } else {
      dispatch(DeleteNodesById, deleteIds);
    }
  },

  [CopyNodes]({ commit, getters, dispatch }, { nodeId, subNodeId = null, copyChildren = false }) {
    const node = getters[GetNodeById]({ id: nodeId });

    if (subNodeId) {
      // keep only the copied sub node
      node.subNodes = { [subNodeId]: node.subNodes[subNodeId] };
    }

    const nodesClipboard = [node];

    if (copyChildren) {
      const parentId = subNodeId ? `${nodeId}_port_${subNodeId}` : nodeId;
      const childNodeIds = getters[GetChildNodesByParentId]({ parentId });
      const childNodes = childNodeIds.map((id) => getters[GetNodeById]({ id }));
      nodesClipboard.push(...childNodes);
    }

    commit(SetNodesClipboard, nodesClipboard);
  },

  [CutNodes]({ commit, getters, dispatch }, { nodeId, subNodeId = null, cutChildren = false }) {
    const parentId = subNodeId ? `${nodeId}_port_${subNodeId}` : nodeId;
    const nodesToDelete = [parentId];

    if (cutChildren) {
      const childNodeIds = getters[GetChildNodesByParentId]({ parentId });
      nodesToDelete.push(...childNodeIds);
    }

    dispatch(CopyNodes, { nodeId, subNodeId, copyChildren: cutChildren });
    dispatch(DeleteNodesById, nodesToDelete);
  },

  [PasteNodes]({ state, commit, getters, dispatch }, { nodeId, subNodeId, side }) {
    const { nodesClipboard } = state;

    if (!nodesClipboard.length) {
      return;
    }

    const conversation = getters[GetCurrentConversation];
    const nodes = [];

    // update clipboard nodes data
    const updateNode = (node, parentNodeId = undefined) => {
      const childrenNodes = findNextNodes(nodesClipboard, node.id);

      // get port id from the old parentId and add them to the new parentId
      // so that the node is connected to the correct parent port
      const portId = node.parentId?.split('_port_')[1] || '';
      const portDetails = portId ? `_port_${portId}` : '';

      // update node id and parent id
      const updatedNode = {
        ...node,
        id: initNodeId(node.type),
        parentId: parentNodeId ? parentNodeId + portDetails : undefined,
      };

      // remove the go to node reference to the current conversation to prevent loops
      if (node.type === NodeType.goTo && node.conversationId === conversation.id) {
        updatedNode.conversationId = '';
      }

      nodes.push(updatedNode);

      childrenNodes.forEach((child) => updateNode(child, updatedNode.id));
    };

    updateNode(nodesClipboard[0]);

    const parentNode = nodes[0];

    if (side === 'top') {
      // set the parentId for the node with nodeId
      dispatch(UpdateNodeData, { data: { id: nodeId, parentId: parentNode.id } });
    } else if (side === 'left' || side === 'right') {
      // future handling for pasting sub nodes in group nodes
    } else {
      // set the parentId for the parentNode of the clipboard nodes
      parentNode.parentId = subNodeId ? `${nodeId}_port_${subNodeId}` : nodeId === 'start-node' ? undefined : nodeId;
    }

    if (nodes.length === 1) {
      dispatch(AddNode, parentNode);
    } else {
      const nodesObj = nodes.reduce((acc, node) => {
        acc[node.id] = node;

        return acc;
      }, {});

      conversation.nodes = updateNodeIndexes({ ...conversation.nodes, ...nodesObj });

      dispatch(UpdateAmpData, { conversations: [conversation] });
    }

    commit(SetNodesClipboard, []);

    // select the clipboard parent node
    commit(SetSelectedNodeId, parentNode.id);
    commit(SetSelectedSubNode, -1);
  },
};

export const getters = {
  /**
   * AMP STORE GETTERS
   * Getters for referencing state from ampStore.
   * @GetCurrentAmp
   */

  [GetCurrentAmp]: (_state, _getters, rootState) => {
    return rootState.ampStore.currentAmp || {};
  },

  /**
   * NODE GETTERS
   * @param {*} state
   * @returns
   */

  [GetAddingNode]: (state) => state.addingNode,

  [GetDragging]: (state) => state.dragging,

  [GetZoom]: (state) => state.zoom,

  [CanUndo]: (state) => state.undoHead,

  [CanRedo]: (state) => state.undoHead < state.undoStack.length,

  [GetUndoChangeset]: (state) => state.undoStack[state.undoHead - 1].undo,

  [GetRedoChangeset]: (state) => state.undoStack[state.undoHead].redo,

  [GetApplyChangesState]: (state) => state.isApplyingChangeset,

  /**
   * CONVERSATION GETTERS
   * @GetCurrentConversationId
   * @GetCurrentConversations convs for currentAmp.
   * @GetCurrentConversation currently selected conv.
   * @GetConversationById conv by specified id.
   * @GetConversationsList gets convs & subconvs sorted based on position and parentId.
   * @GetSelectedSubconversations suboncv parse to sorted array from @GetCurrentConversations
   * @GetNextConversationSortIndex
   * @GetNextLinkSortIndex
   */

  // TODO: SHOULD BE REMOVED, we should use mapState
  [GetCurrentConversationId]: (state) => state.selectedConversationId,

  [GetCurrentConversations](_state, { [GetCurrentAmp]: currentAmp }) {
    return currentAmp?.data.conversations || {};
  },

  [GetCurrentConversation]: (state, { [GetCurrentAmp]: currentAmp }) => {
    return cloneDeep(currentAmp?.data.conversations?.[state.selectedConversationId] || {});
  },

  [GetConversationById]:
    (_state, { [GetCurrentConversations]: currentConversations }) =>
    (id) => {
      return cloneDeep(currentConversations?.[id] || {});
    },

  [GetConversationsList]: (_state) => (conversations) => {
    let index = 0;
    let subindex = 0;
    const sortedConversations = objectToSortedArray(conversations);

    return sortedConversations.reduce((acc, conversation) => {
      if (!conversation.parentId) {
        index++;
        subindex = 0;
        return [...acc, { ...conversation, position: `${index}` }];
      }

      subindex++;
      return [...acc, { ...conversation, position: `${index}.${subindex}` }];
    }, []);
  },

  /**
   * TODO: might want to remove GetCurrentConversationsList
   * and simply use the GetConversationsList getter instead.
   */
  [GetCurrentConversationsList]: (_state, getters) => {
    return getters[GetConversationsList](getters[GetCurrentConversations]);
  },

  [GetSelectedSubconversations]:
    (_state, { [GetCurrentConversations]: conversations }) =>
    (id) => {
      return objectToSortedArray(conversations).filter((conversation) => conversation.parentId === id);
    },

  [GetNextConversationSortIndex]: (_state, { [GetCurrentConversationsList]: conversations }) => {
    return Math.max(...(conversations?.map(({ sortIndex }) => sortIndex) || [0])) + 1;
  },

  [GetNextLinkSortIndex]:
    (_state, { [GetCurrentAmp]: amp }) =>
    (section) => {
      if (!amp.data?.menu?.[section]) return 1;
      const arr = objectToSortedArray(amp.data.menu[section]);
      if (arr.length === 0) return 1;
      return Math.max(...arr.map(({ sortIndex }) => sortIndex)) + 1;
    },

  /**
   * NODES & EDGES GETTERS
   * @GetNodeById gets node by specified id using currentAmp & selectedConversationId.
   * @GetSelectedConversationNodes gets nodes of @GetCurrentConversation
   * @GetConversationNodesById nodes from  @GetConversationById
   * @GetSelectedConversationNodesArray nodes from @GetSelectedConversationNodes parsed by objectToSortedArray.
   * @GetCurrentConversationEdges edges from @GetSelectedConversationNodesArray
   * @GetConversationEdgesById edges from @GetConversationNodesById
   * @GetChildNodesByParentId all nodes after a provided parentId
   */

  [GetSelectedNodeId]: (state) => state.selectedNodeId,

  [GetSelectedSubNode]: (state) => state.selectedSubNodeIndex,

  [GetNodeById]:
    (state, { [GetConversationById]: getConversationById }, rootState) =>
    ({ id, conversationId = state.selectedConversationId, searchAll = false }) => {
      if (id === 'start-node') return {};
      let nodes = {};
      if (searchAll) {
        const conversations = Object.values(rootState.ampStore.currentAmp.data.conversations);
        conversations.forEach((c) => {
          nodes = { ...nodes, ...c.nodes };
        });
      } else {
        nodes = getConversationById(conversationId)?.nodes;
      }

      return nodes?.[id] || {};
    },

  // TODO: This could be removed and simply use the GetCurrentConversation getter.
  [GetSelectedConversationNodes](_state, { [GetCurrentConversation]: currentConversation }) {
    return currentConversation?.nodes || {};
  },

  [GetSelectedConversationNodesArray](_state, { [GetSelectedConversationNodes]: conversationNodes }) {
    return objectToSortedArray(conversationNodes);
  },

  [GetConversationNodesById]:
    (state, { [GetConversationById]: getConversationById }) =>
    (conversationId = state.selectedConversationId) => {
      return objectToSortedArray(getConversationById(conversationId)?.nodes || {});
    },

  [GetCurrentConversationEdges](_state, { [GetSelectedConversationNodesArray]: currentConversationNodes }) {
    return parseToEdges(currentConversationNodes);
  },

  [GetConversationEdgesById]:
    (state, { [GetConversationNodesById]: getConversationNodes }) =>
    (conversationId = state.selectedConversationId) => {
      return parseToEdges(getConversationNodes(conversationId));
    },

  [GetChildNodesByParentId]:
    (state, { [GetConversationNodesById]: getConversationNodes }) =>
    ({ parentId, conversationId = state.selectedConversationId }) => {
      if (!parentId) {
        return [];
      }

      const nodes = getConversationNodes(conversationId);
      const childIds = [];
      const getChildNodes = (currentId) => {
        const children = findNextNodes(nodes, currentId);

        // prevent adding the parent node as a child
        if (currentId !== parentId) {
          childIds.push(currentId);
        }

        children.forEach((child) => getChildNodes(child.id));
      };

      getChildNodes(parentId);

      return childIds;
    },

  /**
   * TRIGGER GETTERS
   * @GetSelectedConversationTriggers
   * @GetTriggers
   */

  [GetSelectedConversationTriggers](_state, { [GetCurrentConversation]: currentConversation }) {
    return currentConversation?.triggers;
  },

  [GetTriggers]:
    (_state, { [GetConversationById]: getConversationById }) =>
    (conversationId) => {
      return getConversationById(conversationId)?.triggers || [];
    },
};
